import React from "react";
import { PageHeader } from "@finitless/shared/components";
import { SUPPORT_CONFIG } from "@finitless/shared/supportConstants";
import { useIntercomContext } from "@finitless/shared/contexts";
import { useUserStore } from "@finitless/shared/stores";
import { usePostHog } from "@finitless/shared/hooks";
import { useTranslation } from "react-i18next";
import { Button, Icon, buttonVariants } from "../ui";
import "./support-ordering.css";

const SupportPage = ({ selectedBrand }) => {
  const { t } = useTranslation("support");
  const { showMessage, isEnabled } = useIntercomContext();
  const user = useUserStore((state) => state.user);
  const { trackFeatureAdoption, trackEvent } = usePostHog();

  const visibleSupportOptions = SUPPORT_CONFIG.supportOptions.filter(
    (option) => option.action !== "feedback" || !!option.url,
  );

  const handleVisitKnowledgeBase = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleSendMessage = () => {
    if (isEnabled) {
      showMessage(); // Opens Intercom with new message composer
      trackFeatureAdoption.liveChatOpened({
        user_id: user?.id,
      });
    }
  };

  const handleFeedbackClick = () => {
    trackEvent("feedback_form_clicked");
  };

  const getLocalizedOptionText = (optionId, field, fallbackText) => {
    const key = `options.${optionId}.${field}`;
    const translated = t(key);
    return translated === key ? fallbackText : translated;
  };

  return (
    <div className="ordering-support-surface flex h-full">
      {/* Main Content Area */}
      <div className="flex-1">
        <PageHeader
          title={t("header.title")}
          selectedBrand={selectedBrand}
          subtitle={t("header.subtitle")}
        />

        <div className="mt-6 space-y-6">
          {/* Support Options Grid */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {visibleSupportOptions.map((option) => (
              <div key={option.id} className="ordering-support-card p-4 sm:p-6">
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    <div className="ordering-support-icon flex h-12 w-12 items-center justify-center rounded-lg">
                      {option.icon === "Lightbulb" ? (
                        <Icon
                          name="Lightbulb"
                          aria-hidden="true"
                          className="h-6 w-6"
                          strokeWidth={1.75}
                        />
                      ) : (
                        <Icon name={option.icon} className="h-6 w-6" />
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-foreground text-lg font-semibold">
                        {getLocalizedOptionText(
                          option.id,
                          "title",
                          option.title,
                        )}
                      </h3>
                      {option.badge ? (
                        <span className="border-primary/20 bg-accent text-primary rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap">
                          {getLocalizedOptionText(
                            option.id,
                            "badge",
                            option.badge,
                          )}
                        </span>
                      ) : null}
                    </div>
                    <p className="text-muted-foreground mt-1 text-sm break-words">
                      {getLocalizedOptionText(
                        option.id,
                        "description",
                        option.description,
                      )}
                    </p>
                    {option.subtitle ? (
                      <p className="text-muted-foreground mt-1 text-sm break-words">
                        {getLocalizedOptionText(
                          option.id,
                          "subtitle",
                          option.subtitle,
                        )}
                      </p>
                    ) : null}

                    {/* Action Buttons */}
                    <div className="mt-4">
                      {option.action === "external" ? (
                        <Button
                          type="button"
                          onClick={() => handleVisitKnowledgeBase(option.url)}
                          emphasis="solid"
                          intent="brand"
                          size="touch"
                          className="w-full whitespace-normal sm:w-auto"
                        >
                          {t("actions.visitKnowledgeBase")}
                        </Button>
                      ) : null}
                      {option.action === "intercom" ? (
                        <Button
                          type="button"
                          onClick={handleSendMessage}
                          emphasis="solid"
                          intent="brand"
                          size="touch"
                          className="w-full whitespace-normal sm:w-auto"
                        >
                          {t("actions.sendMessage")}
                        </Button>
                      ) : null}
                      {option.action === "feedback" ? (
                        <a
                          href={option.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={handleFeedbackClick}
                          className={buttonVariants({
                            variant: "default",
                            size: "touch",
                          })}
                        >
                          {t("actions.giveFeedback")}
                        </a>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportPage;
